import * as React from 'react';
import {
  Button,
  CallListRow,
  Inline,
  Stack,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@relay-towing/ui';
import { Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDriverProfile } from '../../hooks/useDriverProfile';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import { useMember } from '../../hooks/useMember';
import { usePagedList } from '../../hooks/usePagedList';
import { callListParams, callSectionParams } from '../../lib/callLists';
import {
  CALL_SECTIONS,
  callViewsFor,
  headsView,
  type CallSection,
  type CallView,
  type CallViewItem,
  type Member,
} from '../../lib/driverAppTabs';
import { CallsMap, initialsOf, type MapTruck } from '../map/CallsMap';
import { PagedRows } from '../shell/PagedRows';
import { TabScreen } from '../shell/TabScreen';
import { firstLine, locality } from '../status';
import { weightClassOf } from '../vehicle';
import type { CallRecord } from './callRecord';
import { CreateCallSheet } from './CreateCallSheet';

/**
 * How often the calls tab asks again while in view. A call created or
 * assigned elsewhere reaches the app only by asking: there is no live
 * connection yet (Relay_Backend/docs/backlog.md), and the app's 30 seconds
 * left a new call missing from the list long enough to look broken.
 */
const POLL_CALLS_MS = 10_000;

/**
 * One section above a view's calls, asked for only when it heads the view
 * for this member, and shown only while it has a call or the ask failed and
 * says so: an empty section above every driver's calls would be noise. The
 * same list key as the calls: taking an offer refetches both.
 */
function useCallSection(id: CallSection, view: CallViewItem, member: Member) {
  const section = CALL_SECTIONS.find((one) => one.id === id)!;
  const heads = headsView(section, view, member);
  const list = usePagedList<CallRecord>(
    'mobile-calls',
    '/calls',
    callSectionParams(id),
    { enabled: heads, pollEvery: POLL_CALLS_MS }
  );
  return {
    section,
    list,
    shown: heads && (list.isError || list.items.length > 0),
  };
}

/**
 * The calls tab, as the Figma lays it out (143:7883): the switcher as the
 * screen's heading with + Call beside it, the map of the calls in view as a
 * card, then the list, with no search between them. The views are CALL_VIEWS; the calls a member may see that
 * are not theirs, outside offers and unassigned calls, head them as
 * sections of their own (CALL_SECTIONS). The server decides which calls
 * each holds.
 * A driver's map has their own truck on it. Without a working map (no key,
 * or Google refused it) there is no map card, rather than a grey box.
 */
export function CallsScreen() {
  const navigate = useNavigate();
  const member = useMember();
  const { data: profile } = useDriverProfile();
  const { mapsAvailable } = useGoogleMaps();
  const views = callViewsFor(member);
  const [viewId, setViewId] = React.useState<CallView>(views[0].id);
  const view = views.find((one) => one.id === viewId) ?? views[0];
  const [creating, setCreating] = React.useState(false);
  const list = usePagedList<CallRecord>(
    'mobile-calls',
    '/calls',
    callListParams(view.id),
    { pollEvery: POLL_CALLS_MS }
  );
  const sections = [
    useCallSection('offers', view, member),
    useCallSection('unassigned', view, member),
  ].filter(({ shown }) => shown);

  const truck: MapTruck | null =
    member.drives && profile?.locationLatitude && profile.locationLongitude
      ? {
          lat: profile.locationLatitude,
          lng: profile.locationLongitude,
          label: 'Your truck',
          initials: initialsOf(profile.name || 'Me'),
        }
      : null;

  const open = (id: string) => navigate(`/calls/${id}`);
  const row = (call: CallRecord) => (
    <CallListRow
      key={call.id}
      address={firstLine(call.pickupLocation)}
      locality={locality(call.pickupLocation)}
      status={call.status}
      priority={call.priority}
      number={call.dispatchNumber}
      weightClass={weightClassOf(call.towType)}
      onClick={() => open(call.id)}
    />
  );
  // A driver-only member cannot create calls; the server refuses them too.
  const canCreate = member.can('calls.create');

  return (
    <TabScreen>
      <Inline justify="between" align="center">
        <Tabs
          value={view.id}
          onValueChange={(next) => setViewId(next as CallView)}
          appearance="heading"
        >
          <TabsList aria-label="Calls">
            {views.map(({ id, label, icon: ViewIcon }) => (
              <TabsTrigger key={id} value={id}>
                <ViewIcon weight="fill" />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {canCreate ? (
          <Button
            variant="soft"
            size="lg"
            pill
            leadingIcon={<Plus />}
            onClick={() => setCreating(true)}
          >
            Call
          </Button>
        ) : null}
      </Inline>
      <Stack gap={6}>
        {mapsAvailable ? (
          <CallsMap
            className="h-[375px] shrink-0"
            framed
            caption={truck ? 'Truck map' : 'Call map'}
            calls={list.items}
            trucks={truck ? [truck] : []}
            onOpenCall={open}
          />
        ) : null}
        {sections.map(({ section, list: rows }) => (
          <PagedRows
            key={section.id}
            list={rows}
            empty=""
            title={section.label}
          >
            {row}
          </PagedRows>
        ))}
        <PagedRows
          list={list}
          empty={view.empty}
          title={sections.length > 0 ? view.label : undefined}
        >
          {row}
        </PagedRows>
      </Stack>
      {creating ? <CreateCallSheet open onOpenChange={setCreating} /> : null}
    </TabScreen>
  );
}
